/**
 * Token Manager
 * Handles secure storage and retrieval of authentication tokens
 */

const TOKEN_KEY = 'aas_auth_token';
const REFRESH_TOKEN_KEY = 'aas_refresh_token';
const USER_KEY = 'aas_user_data';
const TRUSTED_DEVICE_TOKEN_KEY = 'aas_trusted_device_token';

export type UserRole = 'RegularUser' | 'ClientUser' | 'ClientAdmin';

export interface SecurityPolicies {
  passwordExpirationDays?: number; // undefined means no expiration
  enableApiKeys: boolean;
}

export interface UserData {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  clientId?: string; // Only for ClientUser and ClientAdmin
  passwordExpiresAt?: string; // ISO date string
  passwordExpired?: boolean;
  hasPricingAccess?: boolean;
  isSystemAdmin?: boolean; // System-wide admin privileges (database-managed only)
  canAccessNmvtis?: boolean;
  canAccessTheftLien?: boolean;
  canAccessMotorVehicleRecord?: boolean;
  canAccessVinDecoder?: boolean;
  canAccessAstsPublic?: boolean;
  canAccessInvoicing?: boolean;
  canManageClients?: boolean;
  createdAt: string;
  lastLoginAt?: string;
  enforcedPolicies?: SecurityPolicies; // For Client Users
}

/**
 * Store authentication token
 */
export const setToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store token:', error);
  }
};

/**
 * Retrieve authentication token
 */
export const getToken = (): string | null => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve token:', error);
    return null;
  }
};

/**
 * Remove authentication token
 */
export const removeToken = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove token:', error);
  }
};

/**
 * Store refresh token
 */
export const setRefreshToken = (token: string): void => {
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store refresh token:', error);
  }
};

/**
 * Retrieve refresh token
 */
export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve refresh token:', error);
    return null;
  }
};

/**
 * Remove refresh token
 */
export const removeRefreshToken = (): void => {
  try {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove refresh token:', error);
  }
};

/**
 * Store user data
 */
export const setUserData = (userData: UserData): void => {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
  } catch (error) {
    console.error('Failed to store user data:', error);
  }
};

/**
 * Retrieve user data
 */
export const getUserData = (): UserData | null => {
  try {
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to retrieve user data:', error);
    return null;
  }
};

/**
 * Remove user data
 */
export const removeUserData = (): void => {
  try {
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error('Failed to remove user data:', error);
  }
};

/**
 * Store trusted device token (for Remember Me / MFA bypass)
 */
export const setTrustedDeviceToken = (token: string): void => {
  try {
    localStorage.setItem(TRUSTED_DEVICE_TOKEN_KEY, token);
  } catch (error) {
    console.error('Failed to store trusted device token:', error);
  }
};

/**
 * Retrieve trusted device token
 */
export const getTrustedDeviceToken = (): string | null => {
  try {
    return localStorage.getItem(TRUSTED_DEVICE_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to retrieve trusted device token:', error);
    return null;
  }
};

/**
 * Remove trusted device token
 */
export const removeTrustedDeviceToken = (): void => {
  try {
    localStorage.removeItem(TRUSTED_DEVICE_TOKEN_KEY);
  } catch (error) {
    console.error('Failed to remove trusted device token:', error);
  }
};

/**
 * Clear all authentication data (but preserve trusted device token)
 */
export const clearAuthData = (): void => {
  removeToken();
  removeRefreshToken();
  removeUserData();
  // Pages keep their last results (MVR, VIN, NMVTIS) in sessionStorage; none of it may outlive the session
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear session storage:', error);
  }
  // Note: We intentionally do NOT clear trusted device token on logout
  // This allows "Remember Me" to persist across login sessions
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return getToken() !== null;
};

/**
 * Parse JWT token payload (without verification - server handles verification)
 */
export const parseJwtPayload = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT token:', error);
    return null;
  }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }

  // Check if token expires in the next 5 minutes
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

  return expirationTime - currentTime < bufferTime;
};
